import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { Avatar, Card, Flex, List, Tag, Tooltip, Typography } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import Footer from 'v2/components/Shared/Footer';
import type { IAccess } from 'v2/interfaces/account';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import Header from './Header';

function AccountRow({ access, onSelect }: { access: IAccess; onSelect: (a: IAccess) => void }) {
  const disabled = !access.termsAcceptance && !access.isMe;

  const item = (
    <List.Item
      className={
        disabled
          ? 'pointer-events-none px-4 opacity-50 sm:px-6'
          : 'cursor-pointer px-4 transition-colors hover:bg-(--uizz-antd-color-fill-quaternary) sm:px-6'
      }
      onClick={() => !disabled && onSelect(access)}
      actions={[<Icon.ChevronRight key='go'  />]}
      aria-disabled={disabled}
      aria-label={`Entrar como ${access.name} (${access.email})`}
    >
      <List.Item.Meta
        avatar={
          <Avatar size={48} src={access.photo}>
            {access.name?.at(0)}
          </Avatar>
        }
        title={
          <Flex align='center' gap='small' wrap='wrap'>
            <Typography.Text strong>{access.name}</Typography.Text>
            {access.isMe && <Tag color='blue'>Minha conta</Tag>}
            {disabled && <Tag icon={<Icon.Lock  />}>Aguardando aceite</Tag>}
          </Flex>
        }
        description={<Typography.Text type='secondary'>{access.email}</Typography.Text>}
      />
    </List.Item>
  );

  if (!disabled) return item;

  return (
    <Tooltip
      title={
        <Typography.Text className='text-sm text-[inherit]'>
          O titular desta conta ainda não aceitou a atualização dos nossos Termos de Uso. Solicite a confirmação ao
          responsável; após o aceite, seu acesso será liberado. Dúvidas: suporte@eduzz.com
        </Typography.Text>
      }
      placement='left'
    >
      <div>{item}</div>
    </Tooltip>
  );
}

export default function SelectAccount() {
  const history = useHistory();
  const [accesses] = useObservable(() => eduzzAccountsService.getAccesses(), []);
  const accounts = Array.isArray(accesses) ? accesses : [];

  const onChange = useCallback(
    (a: IAccess) => {
      eduzzAccountsService.changeAccount(a.email);
      history.replace('/');
    },
    [history]
  );

  const sorted = useMemo(() => {
    if (!accesses) return [];
    const copy = [...accesses];
    copy.sort((a, b) => (a.isMe === b.isMe ? a.name.localeCompare(b.name) : a.isMe ? -1 : 1));
    return copy;
  }, [accesses]);

  const isLoading = !accounts.length;

  return (
    <div>
      <div className='eduzz-ui-layout-topbar'>
        <Header />
      </div>
      <main className='bg-surface-default flex min-h-[80vh] flex-col items-center justify-center p-6 text-center'>
        <Flex vertical align='center' gap='small'>
          <Typography.Title level={3} className='mb-0!'>
            Escolha uma conta
          </Typography.Title>
          <Typography.Text type='secondary'>
            {isLoading ? 'Carregando contas...' : 'Você pode alternar a qualquer momento no menu superior'}
          </Typography.Text>
        </Flex>

        <Card className='mt-6 mb-8 w-full max-w-130 text-left' classNames={{ body: 'p-0' }}>
          <List
            loading={isLoading}
            dataSource={sorted}
            aria-label='Lista de contas'
            renderItem={access => <AccountRow key={access.id} access={access} onSelect={onChange} />}
          />
        </Card>

        <Footer hideQuickHelp className='m-0 self-end' />
      </main>
    </div>
  );
}
